import * as angular from "angular";
import { Integration } from "../models/integration.model";
import { IntegrationService } from "../services/integration.service";

export class IntegrationDataProvider {
    public noAudience = ["salesforce", "callhub"];
    public hasGroup = [
        "mailchimp",
        "nationbuilder",
        "salesforce",
        "slack",
        "eventbrite",
        "l2",
        "callhub",
    ];
    public defaultMapping = {
        salesforce: {
            Contact: {
                FirstName: "1",
                LastName: "2",
                OtherStreet: "6",
                OtherCity: "7",
                OtherState: "8",
                OtherPostalCode: "9",
                Email: "13",
                HomePhone: "14",
                MobilePhone: "15",
                OtherLongitude: "25",
                OtherLatitude: "26",
            },
            Lead: {
                FirstName: "1",
                LastName: "2",
                Street: "6",
                City: "7",
                State: "8",
                PostalCode: "9",
                Email: "13",
                Phone: "14",
                MobilePhone: "15",
                Longitude: "25",
                Latitude: "26",
            },
        },
        callhub: {
            firstname: "1",
            lastname: "2",
            mobilenumber: "15",
            phonenumber: "14",
            email: "13",
            address: "6",
            city: "7",
            state: "8",
            zipcode: "9",
        },
    };
    public defaultStatusMapping = {
        callhub: {
            ANSWER: "1",
        },
    };

    public audience: string | null = null;
    public integrations: Integration[] = [];
    public fields: any = null;
    public statuses: any = null;
    public login_fields: any = null;
    public mapping: string[] = [];
    public inProgressCount = 0;
    public inProgressIntegration: Integration;

    public integrationPromise: any;
    public fieldsPromise: any;
    public statusesPromise: any;
    public loginFieldsPromise: any;

    private readonly $q: any;
    private readonly $timeout: any;
    private readonly service: IntegrationService;

    constructor(
        $q: any,
        $timeout: any,
        IntegrationService: IntegrationService
    ) {
        this.$q = $q;
        this.$timeout = $timeout;
        this.service = IntegrationService;
    }

    /**
     * returns all of the data
     */
    getAllIntegrations(latest?: boolean): Promise<Integration[]> {
        const deferred = this.$q.defer();
        if (!!this.integrations.length && !latest) {
            deferred.resolve(this.integrations);
        } else {
            this.service.all().then(
                (response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        let integrations: Integration[] = [];
                        angular.forEach(json.data, (integration: any) => {
                            if (
                                integration.key != "slack" &&
                                integration.key != "eventbrite"
                            ) {
                                integrations.push(new Integration(integration));
                            }
                        });

                        const sortList = [
                            "nationbuilder",
                            "l2",
                            "salesforce",
                            "mailchimp",
                            "callhub",
                        ];

                        const sortedIntegrations = integrations.sort((a, b) => {
                            return (
                                sortList.indexOf(a.key) -
                                sortList.indexOf(b.key)
                            );
                        });

                        this.integrations = sortedIntegrations;
                        deferred.resolve(this.integrations);
                    } else {
                        deferred.reject(json);
                    }
                },
                (error: object) => deferred.reject(error)
            );
        }
        return deferred.promise;
    }

    clearAll(): void {
        this.integrations = [];
        this.audience = null;
        this.mapping = [];
        this.inProgressIntegration = null;
        this.inProgressCount = 0;
    }

    hasGroupSetting(key: any): boolean {
        return this.hasGroup.indexOf(key) > -1;
    }

    getIntegration(key: any): Promise<Integration> {
        const deferred = this.$q.defer();

        this.getAllIntegrations(false)
            .then((integrations: Integration[]) => {
                let integration = integrations.find(
                    (integration: Integration) => integration.key == key
                );
                if (integration) {
                    deferred.resolve(integration);
                } else {
                    deferred.reject({
                        message: "no integration",
                        title: key,
                    });
                }
            })
            .catch((error: object) => deferred.reject(error));
        return deferred.promise;
    }

    updateIntegration(key: string, data: any): void {
        if (this.inProgressIntegration) {
            this.inProgressIntegration.setData(data);
        } else {
            const index = this.integrations.findIndex(
                (integration: Integration) => integration.key == key
            );
            this.integrations[index].setData(data);
        }
    }

    saveMapping(key: string, statuses: any): Promise<any> {
        const deferred = this.$q.defer();
        this.service
            .saveMapping({
                id: key,
                statuses: statuses,
            })
            .then(
                (response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        deferred.resolve(json.data);
                    } else {
                        deferred.reject(json);
                    }
                },
                (error: object) => deferred.reject(error)
            );
        this.integrationPromise = deferred.promise;
        return this.integrationPromise;
    }

    getListViews(key: string): Promise<any> {
        const deferred = this.$q.defer();
        this.service.listViews({ id: key }).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error["data"])
        );
        return deferred.promise;
    }

    getFields(key: any): Promise<any> {
        const deferred = this.$q.defer();
        if (this.fields) {
            deferred.resolve(this.fields);
        } else {
            this.service.fields({ id: key }).then(
                (response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        this.fields = json.data;
                        this.fields = this.formatFields(this.fields);
                        deferred.resolve(json.data);
                    } else {
                        deferred.reject(json);
                    }
                },
                (error: object) => deferred.reject(error)
            );
        }
        this.fieldsPromise = deferred.promise;
        return this.fieldsPromise;
    }

    getStatuses(key: string): Promise<any> {
        const deferred = this.$q.defer();
        if (this.statuses) {
            deferred.resolve(this.statuses);
        } else {
            this.service.statuses({ id: key }).then(
                (response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        this.statuses = json.data;
                        deferred.resolve(this.statuses);
                    } else {
                        deferred.reject(json);
                    }
                },
                (error: object) => deferred.reject(error)
            );
        }
        this.statusesPromise = deferred.promise;
        return this.statusesPromise;
    }

    formatFields(fields: object[]): object[] {
        angular.forEach(fields, (field: any) => {
            if (!field.label) {
                field.label = field.name;
            }
        });
        return fields;
    }

    getLoginFields(key: string): Promise<any> {
        const deferred = this.$q.defer();
        if (this.login_fields) {
            deferred.resolve(this.login_fields);
        } else {
            this.service.loginFields({ id: key }).then(
                (response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        this.login_fields = json.data;
                        deferred.resolve(json.data);
                    } else {
                        deferred.reject(json);
                    }
                },
                (error: object) => deferred.reject(error)
            );
        }
        this.loginFieldsPromise = deferred.promise;
        return this.loginFieldsPromise;
    }

    login(data: any): Promise<any> {
        const deferred = this.$q.defer();
        this.service.login(data).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    getIntegrationDetails(integration: Integration): Promise<any> {
        const deferred = this.$q.defer();
        if (this.noAudience.indexOf(integration.key) > -1) {
            deferred.resolve({ success: true, data: integration });
        } else {
            this.service.audience({ id: integration.key }).then(
                (response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        deferred.resolve(json);
                    } else {
                        deferred.reject({
                            message: "no integration",
                            title: integration.key,
                        });
                    }
                },
                (error: object) => deferred.reject(error)
            );
        }
        return deferred.promise;
    }

    setIntegrationDetails(key: string, audience: string): Promise<any> {
        const deferred = this.$q.defer();
        this.service
            .audienceSet({
                id: key,
                audience: audience,
            })
            .then(
                (response: object) => {
                    const json = response["data"];
                    this.audience = audience;
                    if (json.success) {
                        deferred.resolve(json.data);
                    } else {
                        deferred.reject({
                            message: "error",
                            title: key,
                            audience: audience,
                        });
                    }
                },
                (error: object) => deferred.reject(error)
            );
        return deferred.promise;
    }

    install(key: string): Promise<string> {
        const deferred = this.$q.defer();
        this.service.install({ id: key }).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    pull(key: string): Promise<any> {
        const deferred = this.$q.defer();
        this.service.pull({ id: key }).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    uninstall(key: string, deleteSettings: any): Promise<any> {
        const deferred = this.$q.defer();
        this.service
            .uninstall({ id: key, deleteSettings: deleteSettings })
            .then(
                (response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        this.audience = null;
                        deferred.resolve(json.data);
                    } else {
                        deferred.reject(json);
                    }
                },
                (error: object) => deferred.reject(error)
            );
        return deferred.promise;
    }

    updateSettings(key: string, data: any): Promise<any> {
        const deferred = this.$q.defer();
        if (this.noAudience.indexOf(key) > -1) {
            delete data.audience;
        }

        if (this.hasGroup.indexOf(key) == -1) {
            delete data.group;
        }

        this.service
            .updateSettings({
                id: key,
                settings: data,
            })
            .then(
                (response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        this.updateIntegration(key, json.data);
                        deferred.resolve(json.data);
                    } else {
                        deferred.reject(json);
                    }
                },
                (error: object) => deferred.reject(error)
            );
        return deferred.promise;
    }

    getInProgressIntegration(): Promise<Integration> {
        const deferred = this.$q.defer();
        if (!this.inProgressIntegration) {
            this.getAllIntegrations(true)
                .then((integrations: Integration[]) => {
                    const integration = integrations.find(
                        (integration: any) => {
                            return integration["key"] == "salesforce";
                        }
                    );
                    if (integration) {
                        // this is called twice in quick succession and its killing the binding in the document progress if I don't recheck
                        if (!this.inProgressIntegration) {
                            this.inProgressIntegration = integration;
                            this.updateInProgressCount();
                        }
                        deferred.resolve(this.inProgressIntegration);
                    } else {
                        deferred.reject({
                            message: "no salesforce integration",
                            title: "salesforce",
                        });
                    }
                })
                .catch((error: object) => deferred.reject(error));
        } else {
            deferred.resolve(this.inProgressIntegration);
        }
        return deferred.promise;
    }

    updateInprogressIntegration(socketData: any): void {
        this.$timeout(() => {
            this.inProgressIntegration.update(
                socketData.progress,
                socketData.integration
            );
            this.updateInProgressCount();
        });
    }

    stopInprogressIntegration(socketData: any): void {
        this.$timeout(() => {
            if (socketData.error) {
                this.inProgressIntegration.setError();
            } else {
                this.inProgressIntegration.reset();
            }
            this.updateInProgressCount();
        });
    }

    getProgressCount(): number {
        return this.inProgressCount;
    }

    updateInProgressCount(): void {
        const status = this.inProgressIntegration.status;
        this.inProgressCount =
            status == "Pulling" ||
            status == "Working" ||
            status == "Inserting" ||
            status == "Error"
                ? 1
                : 0;
    }

    processSocketUpdate(socketData: any): void {
        if (!!this.inProgressIntegration) {
            this.updateInprogressIntegration(socketData);
        }
    }

    processSocketDelete(socketData: any): void {
        if (!!this.inProgressIntegration) {
            this.stopInprogressIntegration(socketData);
        }
    }
}

angular
    .module("vcDataProvider")
    .service("IntegrationDataProvider", IntegrationDataProvider);
